import { ShieldAlert } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

export interface AuditLoggedProps {
  label?: string;
  description?: string;
  /** Right-aligned slot — typically the expand/collapse or edit affordance. */
  rightSlot?: ReactNode;
  /** Emphasized chrome — used above expanded sensitive content so the warning never disappears. */
  emphasized?: boolean;
  className?: string;
}

/**
 * Banner that frames any UI surface where the underlying read or write is
 * audit-logged server-side. Pair with the canonical sensitive-information
 * surfaces (PHI reveal, PHI edit, etc.) so the user always sees the warning
 * before and during interaction.
 */
export function AuditLogged({
  label = 'Sensitive information',
  description = 'Access to this section is audit-logged.',
  rightSlot,
  emphasized,
  className,
}: AuditLoggedProps) {
  return (
    <div
      role="note"
      className={cn(
        'flex items-start gap-3 rounded-sm border px-3 py-2',
        emphasized
          ? 'border-warning-300 bg-warning-50 text-warning-700'
          : 'border-neutral-200 bg-neutral-50 text-neutral-700',
        className,
      )}
    >
      <ShieldAlert
        className={cn(
          'mt-0.5 h-4 w-4 shrink-0',
          emphasized ? 'text-warning-700' : 'text-neutral-500',
        )}
        strokeWidth={1.5}
        aria-hidden="true"
      />
      <div className="flex-1">
        <div className="text-meta-sm font-medium">{label}</div>
        <div className="text-meta-sm text-neutral-600">{description}</div>
      </div>
      {rightSlot ? <div className="flex shrink-0 items-center gap-2">{rightSlot}</div> : null}
    </div>
  );
}
